import { Injectable } from "@angular/core";
import {
  ActivatedRouteSnapshot,
  Route,
  Router,
  RouterStateSnapshot,
  UrlSegment,
  UrlTree,
} from "@angular/router";

import { AuthService } from "#root/core/auth/auth.service";
import { Observable, of, switchMap } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class AuthGuard {
  /**
   * Constructor
   */
  constructor(
    private _authService: AuthService,
    private _router: Router,
  ) {}

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Can activate
   *
   * @param route
   * @param state
   */
  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Observable<boolean> | Promise<boolean> | boolean {
    const redirectUrl = state.url === "/sign-out" ? "/" : state.url;
    return this._check(redirectUrl);
  }

  /**
   * Can activate child
   *
   * @param childRoute
   * @param state
   */
  canActivateChild(
    childRoute: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ):
    | Observable<boolean | UrlTree>
    | Promise<boolean | UrlTree>
    | boolean
    | UrlTree {
    const stateParsed = state.url.includes("error") ? "/" : state.url;
    const redirectUrl = state.url === "/sign-out" ? "/" : stateParsed;
    return this._check(redirectUrl);
  }

  /**
   * Can load
   *
   * @param route
   * @param segments
   */
  canLoad(
    route: Route,
    segments: UrlSegment[],
  ): Observable<boolean> | Promise<boolean> | boolean {
    return this._check("/");
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Private methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Check the authenticated status
   *
   * @param redirectURL
   * @private
   */
  private _check(redirectURL: string): Observable<boolean> {
    return this._authService.check().pipe(
      switchMap((authenticated) => {
        if (!authenticated) {
          const stateParsed = redirectURL.includes("error") ? "/" : redirectURL;
          this._router.navigate(["sign-in"], {
            queryParams: { redirectURL: stateParsed },
          });
          return of(false);
        }

        // Feature flag: temporalmente desactivado el forzado de cambio de contraseña.
        // Poner en true para reactivar la lógica de passwordChangedBySelf.
        const FORCE_PASSWORD_CHANGE_ENABLED = false;
        if (!FORCE_PASSWORD_CHANGE_ENABLED) {
          // Si intenta entrar a force-password-change, lo mandamos al home.
          if (redirectURL.includes("force-password-change")) {
            this._router.navigate(["/"]);
            return of(false);
          }
          return of(true);
        }

        const passwordChangedBySelf: boolean =
          localStorage.getItem("passwordChangedBySelf") === "true"
            ? true
            : false;

        if (passwordChangedBySelf === false) {
          if (
            redirectURL.includes("force-password-change") ||
            redirectURL.includes("sign-out")
          ) {
            return of(true);
          }
          this._router.navigate(["/force-password-change"]);
          return of(false);
        }

        if (
          passwordChangedBySelf === true &&
          redirectURL.includes("force-password-change")
        ) {
          this._router.navigate(["/"]);
          return of(false);
        }

        return of(true);
      }),
    );
  }
}
